import React, { useState, useMemo } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const DischargeSummaryList = ({ 
    records = [], 
    onAdd, 
    onEdit, 
    onDelete,
    isLoading = false 
}) => {
    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // ✅ Filtered Records
    const filteredRecords = useMemo(
        () =>
            records.filter((r) =>
                Object.values(r).some((v) =>
                    String(v ?? "")
                        .toLowerCase()
                        .includes(filterText.toLowerCase())
                )
            ),
        [records, filterText]
    );

    const totalRows = filteredRecords.length;
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    // ✅ Delete Confirmation
    const handleDelete = (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This Discharge Summary record will be permanently deleted!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        }).then((result) => {
            if (result.isConfirmed) {
                onDelete(id);
            }
        });
    };

    // ✅ Export PDF
    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Discharge Summary Records", 14, 10);
        const tableColumn = [
            "#",
            "Client Name",
            "Program",
            "Admission Date",
            "Discharge Date",
            "Reason for Discharge",
            "Reviewed By",
        ];
        const tableRows = filteredRecords.map((r, index) => [
            index + 1,
            r.client_name,
            r.program,
            r.admission_date,
            r.discharge_date,
            r.reason,
            r.reviewed_by,
        ]);
        autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20 });
        doc.save("discharge_summary.pdf");
    };

    // ✅ Custom Pagination
    const CustomPagination = ({
        onChangePage,
        onChangeRowsPerPage,
        currentPage,
        rowsPerPage,
        rowCount,
    }) => {
        const totalPages = Math.ceil(rowCount / rowsPerPage);
        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                        Rows per page:
                    </span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px", fontSize: "0.875rem" }}
                        value={rowsPerPage}
                        onChange={(e) =>
                            onChangeRowsPerPage(Number(e.target.value), currentPage)
                        }
                    >
                        {[5, 10, 15, 20].map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage - 1, rowCount)}
                        disabled={currentPage === 1}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage + 1, rowCount)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    // ✅ Columns
    const columns = [
        {
            name: "S.No",
            cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
            width: "80px",
        },
        { 
            name: "Client Name", 
            selector: (r) => r.client_name, 
            sortable: true 
        },
        { 
            name: "Program", 
            selector: (r) => r.program, 
            sortable: true 
        },
        { 
            name: "Admission Date", 
            selector: (r) => r.admission_date?.split("T")[0] || "N/A", 
            sortable: true 
        },
        { 
            name: "Discharge Date", 
            selector: (r) => r.discharge_date?.split("T")[0] || "N/A", 
            sortable: true 
        },
        { 
            name: "Reason for Discharge", 
            selector: (r) => r.reason, 
            sortable: true 
        },
        { 
            name: "Reviewed By", 
            selector: (r) => r.reviewed_by, 
            sortable: true 
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-1">
                    {onEdit && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="Edit"
                            onClick={() => onEdit(row.id)}
                            disabled={isLoading}
                        >
                            <i className="ri-edit-line"></i>
                        </button>
                    )}
                    <button
                        className="btn btn-sm btn-outline-danger"
                        title="Delete"
                        onClick={() => handleDelete(row.id)}
                        disabled={isLoading}
                    >
                        <i className="ri-delete-bin-line"></i>
                    </button>
                </div>
            ),
            width: "160px",
            ignoreRowClick: true,
            button: true,
        },
    ];

    return (
        <div>
            {/* === Header === */}
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                {/* Left: Export + Search */}
                <div className="d-flex align-items-center gap-2 position-relative">
                    <div className="dropdown">
                        <button
                            className="btn btn-label-primary btn-sm dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            disabled={isLoading || filteredRecords.length === 0}
                        >
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li>
                                <button 
                                    className="dropdown-item" 
                                    onClick={handleExportPDF}
                                    disabled={filteredRecords.length === 0}
                                >
                                    <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                                </button>
                            </li>
                            <li>
                                <CSVLink
                                    data={filteredRecords}
                                    filename="discharge_summary.csv"
                                    className="dropdown-item"
                                    onClick={(e) => {
                                        if (filteredRecords.length === 0) {
                                            e.preventDefault();
                                            Swal.fire("Info", "No data to export", "info");
                                        }
                                    }}
                                >
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                        </ul>
                    </div>

                    <input
                        type="text"
                        placeholder="Search discharge summary..."
                        className="form-control form-control-sm"
                        style={{ width: "250px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        disabled={isLoading}
                    />
                </div>

                {/* Center Info */}
                <h6 className="mb-0 text-center text-muted flex-grow-1">
                    {isLoading ? (
                        "Loading records..."
                    ) : (
                        `Showing ${start}-${end} of ${totalRows} records`
                    )}
                </h6>

                {/* Right: Add Button */}
                <div>
                    {onAdd && (
                        <button
                            className="btn btn-sm btn-primary"
                            title="Add New"
                            onClick={onAdd}
                            disabled={isLoading}
                        >
                            <i className="ri-add-line me-1"></i> Add New
                        </button>
                    )}
                </div>
            </div>

            {/* === Table === */}
            <div className="card-body p-2">
                <DataTable
                    columns={columns}
                    data={filteredRecords}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={totalRows}
                    onChangeRowsPerPage={setRowsPerPage}
                    onChangePage={setCurrentPage}
                    paginationComponent={CustomPagination}
                    highlightOnHover
                    striped
                    responsive
                    progressPending={isLoading}
                    progressComponent={
                        <div className="p-3 text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted">Loading discharge summaries...</p>
                        </div>
                    }
                    noDataComponent={
                        <div className="p-3 text-muted text-center">
                            <i className="ri-file-list-3-line fs-1 text-muted d-block mb-2"></i>
                            {isLoading ? "Loading records..." : "No discharge summary records found"}
                        </div>
                    }
                />
            </div>
        </div>
    );
};

export default DischargeSummaryList;